import type { DocumentFolder } from '../types'
import type { CoOwnerFolderLink } from '../hooks/useCoOwnerFolders'

/**
 * Trouve le dossier système parent d'un dossier donné
 * Remonte récursivement la hiérarchie jusqu'à trouver un dossier système (is_system_folder = true)
 *
 * @param folderId - L'ID du dossier dont on cherche le parent système
 * @param folders - La liste complète des dossiers
 * @returns Le dossier système parent, ou undefined si non trouvé
 */
export function findSystemFolderParent(
  folderId: string | null,
  folders: DocumentFolder[]
): DocumentFolder | undefined {
  if (!folderId) return undefined

  const folder = folders.find((f) => f.id === folderId)
  if (!folder) return undefined

  // Si c'est déjà un dossier système, on le retourne
  if (folder.is_system_folder) {
    return folder
  }

  // Sinon on remonte au parent
  if (folder.parent_folder_id) {
    return findSystemFolderParent(folder.parent_folder_id, folders)
  }

  // Pas de parent et pas système -> pas de parent système
  return undefined
}

/**
 * Construit le chemin complet d'un dossier (array d'objets { id, name })
 *
 * @param folderId - L'ID du dossier
 * @param folders - La liste complète des dossiers
 * @param coOwnerFolders - Liste des liens symboliques vers les dossiers copropriétaires (optionnel)
 * @returns Le chemin complet sous forme d'array
 */
export function buildFolderPath(
  folderId: string | null,
  folders: DocumentFolder[],
  coOwnerFolders?: CoOwnerFolderLink[]
): Array<{ id: string; name: string }> {
  if (!folderId) return []

  const folder = folders.find((f) => f.id === folderId)

  // Si le dossier n'est pas trouvé dans la liste des dossiers de la résidence,
  // c'est peut-être un dossier physique de copropriétaire
  if (!folder && coOwnerFolders) {
    const coOwnerLink = coOwnerFolders.find((co) => co.linked_folder_id === folderId)
    if (coOwnerLink) {
      // Trouver le dossier système "Copropriétaires" dans la résidence
      const coOwnerSystemFolder = folders.find(
        (f) => f.suggested_category === 'coproprietaire' || f.name === 'Copropriétaires'
      )

      if (coOwnerSystemFolder) {
        // Retourner le chemin : Copropriétaires > Nom du copropriétaire
        return [
          { id: coOwnerSystemFolder.id, name: coOwnerSystemFolder.name },
          { id: folderId, name: coOwnerLink.folder_name },
        ]
      }

      // Fallback : juste le nom du copropriétaire
      return [{ id: folderId, name: coOwnerLink.folder_name }]
    }
  }

  if (!folder) return []

  const path: Array<{ id: string; name: string }> = []
  let current: DocumentFolder | undefined = folder

  while (current) {
    path.unshift({ id: current.id, name: current.name })

    if (current.parent_folder_id) {
      current = folders.find((f) => f.id === current!.parent_folder_id)
    } else {
      break
    }
  }

  return path
}
